import {
  applyFullViewportPrintSectionLock,
  resetFullViewportPrintInnerElements,
  resolveFullViewportInnerSelector,
} from './reportPrintFitViewportLock.util';

/** Usable height for one US Letter page at 96dpi with @page margin 0. */
export const REPORT_PRINT_PAGE_HEIGHT_PX = 1056;

/** Slight buffer so scaled content does not clip at page edge. */
export const REPORT_PRINT_PAGE_SAFE_PX = 1008;

const FIT_INNER_SELECTOR = '.report-print-section__fit';
const PRINT_SAFE_TOP_SELECTOR = '.report-print-section__safe-top';
const PRINT_HEADER_SELECTOR = '.report-print-section__header';
const PRINT_SCALABLE_SELECTOR = '.report-section-print-scalable';
const PRINT_TAIL_BAND_SELECTOR =
  '.report-section-callout-band, .report-section-insight-band';

const MIN_PRINT_SCALE = 0.18;
/** Bumped on reset so deferred {@link scheduleReportPrintSectionFit} runs cannot apply after print. */
let activePrintFitGeneration = 0;

/**
 * @returns Next generation id; invalidates in-flight scheduled fits.
 */
const bumpPrintFitGeneration = (): number => {
  activePrintFitGeneration += 1;
  return activePrintFitGeneration;
};

/**
 * @param fit - Inner print wrapper.
 * @returns Content height in CSS pixels after layout.
 */
const measurePrintContentHeight = (fit: HTMLElement): number => {
  return fit.scrollHeight;
};

type HeaderScalableMeasure = {
  header: HTMLElement;
  headerHeight: number;
  scalable: HTMLElement;
  scalableHeight: number;
};

/**
 * @param fit - Inner print wrapper.
 * @returns Header + scalable regions when the standard layout is present.
 */
const measureHeaderAndScalable = (
  fit: HTMLElement,
): HeaderScalableMeasure | null => {
  const header = fit.querySelector<HTMLElement>(PRINT_HEADER_SELECTOR);
  const scalable = fit.querySelector<HTMLElement>(PRINT_SCALABLE_SELECTOR);
  if (!header || !scalable) {
    return null;
  }
  return {
    header,
    headerHeight: header.offsetHeight,
    scalable,
    scalableHeight: measurePrintContentHeight(scalable),
  };
};

/**
 * @param band - Callout or insight tail band element.
 * @returns Content height for print-fit budget (ignores flex-grown empty tan).
 */
const measurePrintTailBandContentHeight = (band: HTMLElement): number => {
  if (band.classList.contains('report-section-insight-band')) {
    const container = band.querySelector<HTMLElement>('.container');
    if (container) {
      const style = getComputedStyle(band);
      const padTop = Number.parseFloat(style.paddingTop) || 0;
      const padBottom = Number.parseFloat(style.paddingBottom) || 0;
      return container.offsetHeight + padTop + padBottom;
    }
  }
  return band.offsetHeight;
};

/**
 * @param fit - Inner print wrapper.
 * @returns Combined height of callout / insight bands below the hosted body.
 */
const measurePrintTailBandHeight = (fit: HTMLElement): number => {
  let total = 0;
  fit.querySelectorAll<HTMLElement>(PRINT_TAIL_BAND_SELECTOR).forEach(band => {
    total += measurePrintTailBandContentHeight(band);
  });
  return total;
};

/**
 * @param section - Outer print page section.
 * @returns Safe-top spacer height outside the fit wrapper.
 */
const measurePrintSafeTopHeight = (section: HTMLElement): number => {
  const safeTop = section.querySelector<HTMLElement>(PRINT_SAFE_TOP_SELECTOR);
  if (!safeTop) {
    return 0;
  }
  return safeTop.getBoundingClientRect().height;
};

/**
 * @param section - Outer print section element.
 * @param defaultPageHeightPx - Default printable height.
 * @returns Height budget for fit comparison.
 */
const pageHeightBudgetForSection = (
  section: HTMLElement,
  defaultPageHeightPx: number,
): number => {
  const safeTop = measurePrintSafeTopHeight(section);
  return defaultPageHeightPx - safeTop;
};

/**
 * @param section - Outer print section element.
 * @param rawScale - Unclamped scale ratio.
 * @returns Scale clamped for readability and one-page fit.
 */
const clampPrintScale = (section: HTMLElement, rawScale: number): number => {
  const minAttr = section.getAttribute('data-report-print-min-scale');
  const parsedMin = minAttr ? Number.parseFloat(minAttr) : Number.NaN;
  const minScale = Number.isFinite(parsedMin) ? parsedMin : MIN_PRINT_SCALE;
  return Math.max(minScale, Math.min(1, rawScale));
};

/**
 * @param fit - Inner print wrapper.
 * @returns Whether this section uses natural height instead of forced 100vh.
 */
const usesNaturalPageHeight = (fit: HTMLElement): boolean => {
  return measureHeaderAndScalable(fit) !== null;
};

/**
 * Scales each `.report-print-section` so its content fits within one printed page.
 * @param pageHeightPx - Printable height in CSS pixels.
 */
export const fitReportPrintSections = (
  pageHeightPx: number = REPORT_PRINT_PAGE_SAFE_PX,
): void => {
  const sections = document.querySelectorAll<HTMLElement>('.report-print-section');

  sections.forEach(section => {
    const fit = section.querySelector<HTMLElement>(FIT_INNER_SELECTOR);
    if (!fit) {
      return;
    }

    resetReportPrintSectionElement(section, fit);

    const sectionId = section.getAttribute('data-report-section-id');
    const fullViewportInnerSelector = resolveFullViewportInnerSelector(sectionId);
    if (fullViewportInnerSelector) {
      applyFullViewportPrintSectionLock(section, fit, fullViewportInnerSelector);
      return;
    }

    const headerScalable = measureHeaderAndScalable(fit);
    const naturalHeight = usesNaturalPageHeight(fit);
    const safeTopHeight = measurePrintSafeTopHeight(section);
    const tailBandHeight = measurePrintTailBandHeight(fit);

    const contentHeight = headerScalable
      ? safeTopHeight +
        headerScalable.headerHeight +
        headerScalable.scalableHeight +
        tailBandHeight
      : safeTopHeight + measurePrintContentHeight(fit);

    const pageBudget = pageHeightBudgetForSection(section, pageHeightPx);

    if (contentHeight <= pageBudget) {
      section.style.minHeight = naturalHeight ? 'auto' : '100vh';
      return;
    }

    const rawScale = pageBudget / contentHeight;
    const scale = clampPrintScale(section, rawScale);

    if (headerScalable) {
      applyPrintScale(
        section,
        headerScalable.scalable,
        scale,
        headerScalable.scalableHeight,
        naturalHeight,
      );
      return;
    }

    applyPrintScale(section, fit, scale, contentHeight - safeTopHeight, naturalHeight);
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('report-print-fit-complete'));
  }
};

/**
 * @param section - Outer print page section.
 * @param fit - Inner wrapper to scale.
 * @param scale - Scale factor (0–1).
 * @param contentHeight - Unscaled content height used for transform fallback.
 * @param naturalPageHeight - When true, do not force `min-height: 100vh` on the section.
 */
const applyPrintScale = (
  section: HTMLElement,
  fit: HTMLElement,
  scale: number,
  contentHeight: number,
  naturalPageHeight = false,
): void => {
  const scalesHostedBodyOnly = fit.classList.contains(
    PRINT_SCALABLE_SELECTOR.replace('.', ''),
  );

  section.style.minHeight = naturalPageHeight ? 'auto' : '100vh';
  section.style.height = 'auto';
  section.style.maxHeight = 'none';
  section.style.overflow = 'visible';

  fit.style.zoom = String(scale);
  fit.style.transform = '';
  fit.style.width = '';
  fit.style.height = '';
  fit.style.marginBottom = '';
  fit.style.overflow = 'visible';

  if (scale >= 1) {
    fit.style.zoom = '';
    return;
  }

  const marginCollapse = contentHeight * (scale - 1);
  const scaledHeight = Math.ceil(contentHeight * scale);
  const zoomNumeric = Number.parseFloat(fit.style.zoom);

  if (Number.isFinite(zoomNumeric) && zoomNumeric > 0) {
    fit.style.marginBottom = `${marginCollapse}px`;
    return;
  }

  fit.style.zoom = '';
  fit.style.transform = `scale(${scale})`;
  fit.style.transformOrigin = 'top left';
  fit.style.width = `${100 / scale}%`;
  fit.style.height = `${scaledHeight}px`;
  fit.style.overflow = 'hidden';
  fit.style.marginBottom = `${marginCollapse}px`;

  if (!scalesHostedBodyOnly) {
    section.style.height = `${scaledHeight}px`;
    if (!naturalPageHeight) {
      section.style.overflow = 'hidden';
    }
  }
};

/**
 * Ensures section banner images are decoded before print (lazy below-the-fold imgs).
 */
const ensureReportBannerImagesReady = (): Promise<void> => {
  const images = document.querySelectorAll<HTMLImageElement>('.banner__icon-img');
  const waits = Array.from(images).map(
    img =>
      new Promise<void>(resolve => {
        if (img.complete && img.naturalWidth > 0) {
          resolve();
          return;
        }
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      }),
  );
  return Promise.all(waits).then(() => undefined);
};

/**
 * Runs {@link fitReportPrintSections} after layout (charts, fonts).
 * @param pageHeightPx - Printable height in CSS pixels.
 */
export const scheduleReportPrintSectionFit = (
  pageHeightPx: number = REPORT_PRINT_PAGE_SAFE_PX,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const generation = bumpPrintFitGeneration();
  window.requestAnimationFrame(() => {
    if (generation !== activePrintFitGeneration) {
      return;
    }
    window.requestAnimationFrame(() => {
      if (generation !== activePrintFitGeneration) {
        return;
      }
      window.setTimeout(() => {
        if (generation !== activePrintFitGeneration) {
          return;
        }
        void ensureReportBannerImagesReady().then(() => {
          if (generation !== activePrintFitGeneration) {
            return;
          }
          window.scrollTo(0, 0);
          fitReportPrintSections(pageHeightPx);
        });
      }, 120);
    });
  });
};

/**
 * Clears inline fit styles after print or before re-measuring.
 */
export const resetReportPrintSectionFit = (): void => {
  bumpPrintFitGeneration();
  document.querySelectorAll<HTMLElement>('.report-print-section').forEach(section => {
    const fit = section.querySelector<HTMLElement>(FIT_INNER_SELECTOR);
    if (fit) {
      resetReportPrintSectionElement(section, fit);
    }
  });
};

/**
 * @returns Map of section id → applied scale (1 when unscaled).
 */
export const getReportPrintSectionScales = (): Map<string, number> => {
  const scales = new Map<string, number>();
  document.querySelectorAll<HTMLElement>('.report-print-section').forEach(section => {
    const sectionId = section.getAttribute('data-report-section-id') ?? 'unknown';
    const fit = section.querySelector<HTMLElement>(FIT_INNER_SELECTOR);
    const scalable = section.querySelector<HTMLElement>(PRINT_SCALABLE_SELECTOR);
    const scaledEl = scalable ?? fit;
    if (!scaledEl) {
      scales.set(sectionId, 1);
      return;
    }
    const zoom = Number.parseFloat(scaledEl.style.zoom);
    if (Number.isFinite(zoom) && zoom > 0) {
      scales.set(sectionId, zoom);
      return;
    }
    const transform = scaledEl.style.transform;
    const match = transform.match(/scale\(([\d.]+)\)/);
    if (match) {
      scales.set(sectionId, Number.parseFloat(match[1]));
      return;
    }
    scales.set(sectionId, 1);
  });
  return scales;
};

const resetPrintFitStyles = (element: HTMLElement): void => {
  element.style.zoom = '';
  element.style.transform = '';
  element.style.transformOrigin = '';
  element.style.width = '';
  element.style.height = '';
  element.style.marginBottom = '';
  element.style.overflow = '';
  element.style.maxHeight = '';
  element.style.minHeight = '';
};

const resetReportPrintSectionElement = (
  section: HTMLElement,
  fit: HTMLElement,
): void => {
  resetPrintFitStyles(fit);
  fit.querySelectorAll<HTMLElement>(PRINT_SCALABLE_SELECTOR).forEach(scalable => {
    resetPrintFitStyles(scalable);
  });
  resetFullViewportPrintInnerElements(fit, resetPrintFitStyles);
  section.style.height = '';
  section.style.minHeight = '';
  section.style.maxHeight = '';
  section.style.overflow = '';
  section.style.paddingTop = '';
  section.style.boxSizing = '';
};
